import React, { useRef, useState } from "react";
import { IoMdSearch } from "react-icons/io";
import PopularityFIlter from "../PopularityFIlter";
import DropdownWithCheckboxes from "../DropdownWithCheckboxes";
import { useTranslation } from "react-i18next";
import { ApiPost } from "../../helper/axiosFunctions";
import { useEffect } from "react";
import Loading from "../Loading";
import { commonToast } from "../../helper/Toast";
import DeckCard, { DeckCardSkeleton } from '../DeckCard'
import SubjectFilter from '../SubjectFilter'

export default function Zubroteka() {
  const { t } = useTranslation();
  const [data, setData] = useState([])
  const [loading, setLoading] = useState(false)
  const [isExpand, setIsExpand] = useState(false);
  const [lastId, setLastId] = useState("");
  const [hasMore, setHasMore] = useState(true);
  const [search, setSearch] = useState('')
  const [selectedSubject, setSelectedSubject] = useState(null);
  const [apiError, setApiError] = useState(false);
  const [filter, setFilter] = useState([
    {
      title: t("filter.popularity"),
      isActive: true,
    },
    {
      title: t("filter.newest"),
      isActive: false,
    },
    {
      title: t("filter.recently_watched"),
      isActive: false,
    },
  ]);
  const observer = useRef(null);
  const isFirstRender = useRef(true);
  const handleSearch = (e) => {
    setSearch(e.target.value)
  }

  const GetDeckData = async (reset = false, depth = 0) => {
    if (!hasMore && !reset) return;
    if (apiError) return;

    setLoading(true);
    try {
      const url = reset || !lastId
        ? `/contents/decks/search?pageSize=8`
        : `/contents/decks/search?lastId=${lastId}&pageSize=8`;

      console.log('Making API request to:', url);
      const requestBody = {
        deckName: search || "",
        countries:["LT"],
        "status":  "PUBLISHED"
      };
      
      // Add subject filter if selected
      if (selectedSubject) {
        requestBody.mainSubject = selectedSubject;
      }
      
      const response = await ApiPost(url, requestBody);

      const rawDecks = response?.data || [];
      console.log('Raw API response:', rawDecks);
      
      // Log statuses before filtering
      console.log('Deck statuses before filtering:', rawDecks.map(deck => ({
        id: deck.deckId,
        name: deck.deckName,
        status: deck?.status
      })));

      // Filter out deleted decks (handle different possible status values)
      const newDecks = rawDecks.filter(deck => {
        const status = deck?.status?.toUpperCase();
        // List of statuses that should be hidden
        const hiddenStatuses = ["DELETED", "INACTIVE", "REMOVED", "ARCHIVED", "DISABLED", "HIDDEN"];
        const shouldHide = hiddenStatuses.includes(status);
        console.log(`Deck ${deck.deckId} (${deck.deckName}) - Status: ${status}, Hidden: ${shouldHide}`);
        return !shouldHide;
      });

      console.log('Filtered decks:', newDecks.map(deck => ({
        id: deck.deckId,
        name: deck.deckName,
        status: deck?.status
      })));

      // If current page contains only hidden/deleted decks, automatically fetch the next page (max 5 consecutive skips)
      if (newDecks.length === 0 && rawDecks.length > 0 && depth < 5) {
        const nextLastId = rawDecks[rawDecks.length - 1]._id;
        setLastId(nextLastId);
        await GetDeckData(reset, depth + 1);
        return;
      }

      if (reset) {
        setData(newDecks);
        setLastId(rawDecks.length > 0 ? rawDecks[rawDecks.length - 1]._id : "");
        setHasMore(rawDecks.length > 0);
      } else {
        if (newDecks.length > 0) {
          setData((prevData) => [...prevData, ...newDecks]);
        }
        setLastId(rawDecks.length > 0 ? rawDecks[rawDecks.length - 1]._id : lastId);
        setHasMore(rawDecks.length > 0);
      }
      setApiError(false);
    } catch (error) {
      console.error('API Error:', error);
      setApiError(true);
      setHasMore(false);
      commonToast(error?.data?.error || error?.message, "error");
    } finally {
      setLoading(false);
    }
  };

  const handleFilterClick = (index) => {
    const updatedFilter = filter.map((item, i) => ({
      ...item,
      isActive: i === index,
    }));
    setFilter(updatedFilter);
  };

  const handleSubjectChange = (subject) => {
    setSelectedSubject(subject);
    // Reset pagination when subject changes
    setLastId("");
    setHasMore(true);
  };

  //for search filter and subject filter
  useEffect(() => {
    window.scrollTo(0, 0);
    if (isFirstRender.current && search.length === 0 && !selectedSubject) {
      isFirstRender.current = false;
      return;
    }
    if (apiError) return;

    const delayDebounce = setTimeout(() => {
      GetDeckData(true);
    }, 500);

    return () => clearTimeout(delayDebounce);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, selectedSubject]);

  //for pagination
  useEffect(() => {
    if (loading || !hasMore || apiError) return;

    observer.current = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          GetDeckData();
        }
      },
      { threshold: 1.0 }
    );

    if (observer.current && document.getElementById("loadMoreTrigger")) {
      observer.current.observe(document.getElementById("loadMoreTrigger"));
    }

    return () => observer.current?.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, hasMore]);

  return (
    <div className="p-10 md:p-5">
      <div className="flex md:flex-col justify-between items-center">
        <h1 className="text-2xl font-semibold">{t('zubroteka')}</h1>
        <div className="flex items-center gap-3 md:text-center text-nowrap overflow-auto xxxs:w-full border rounded-full px-2 py-1.5 md:mt-3">
          {[t("catalog"), t("Favorites"), t("useit")]?.map((item) => (
            <div
              className={`cursor-pointer ${item === t("catalog") &&
                "bg-darkblue text-white rounded-full px-3 py-1.5"
                } px-3`}
            >
              {item}
            </div>
          ))}
        </div>
      </div>
      <div className="flex border-t pt-5 mt-5 flex-wrap justify-between items-center my-3">
        <div className="flex flex-wrap items-center gap-3 text-sm">
          <div
            className=" h-10 relative border cursor-pointer rounded-full flex  items-center transition-width duration-300"
            onClick={() => setIsExpand(true)}
          >
            <IoMdSearch size={24} className="absolute left-2" />
            <input
              type="text"
              value={search}
              onChange={(e) => handleSearch(e)}
              className={`${isExpand ? "w-full" : "w-10"
                } transition-all duration-100 
                ease-out h-10 border rounded-full ps-10`}
            />
          </div>
          <div className="border rounded-full h-10 flex justify-center items-center">
            <DropdownWithCheckboxes isExpand={isExpand} />
          </div>
          <div className="border rounded-full h-10 flex justify-center items-center px-3">
            {t("allages")}
          </div>
          <div className="border rounded-full h-10 flex justify-center items-center px-3">
            {t("Allauthors")}
          </div>
        </div>
        <div className="flex items-center gap-3">
          <div className="border rounded-full h-10 flex justify-center items-center md:mb-4">
            <SubjectFilter onSubjectChange={handleSubjectChange} />
          </div>
          <div className="border rounded-full h-10 flex justify-center items-center">
            <PopularityFIlter
              filter={filter}
              setFilter={setFilter}
              handleFilterClick={handleFilterClick}
            />
          </div>
        </div>
      </div>
      <div className="my-10">
        {data.length > 0 ? (
          <div className="grid gap-5 grid-cols-4 xl:grid-cols-3 md:!grid-cols-1">
            {data.map((item) => (
              <DeckCard
                key={item.deckId || item._id}
                deck={item}
                to={`/subject/${item.deckId}`}
                showLanguage={Boolean(item?.language)}
              />
            ))}
          </div>
        ) : (
          !loading && !apiError && <p className="text-center text-2xl mt-20 text-gray-500">No Data Found</p>
        )}
        {loading && data.length === 0 && (
          <div className="grid gap-5 grid-cols-4 xl:grid-cols-3 md:!grid-cols-1">
            {Array.from({ length: 8 }).map((_, i) => (
              <DeckCardSkeleton key={i} />
            ))}
          </div>
        )}
        {loading && data.length > 0 && <Loading size={10} />}
        {apiError && (
          <div className="text-center mt-20">
            <p className="text-2xl text-red-500">Error loading data</p>
            <button
              onClick={() => {
                setApiError(false);
                GetDeckData(true);
              }}
              className="mt-4 px-4 py-2 bg-darkblue text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Retry
            </button>
          </div>
        )}
        {hasMore && !apiError && <div id="loadMoreTrigger" className="h-10"></div>}
      </div>

    </div>
  );
}
